export interface InterchangeFee {
	id?: string;
	name: string;
	service?: string;
	amountEur: number;
	rate?: number;
	flat?: number;
	rateDescription?: string;
	basis?: string;
	source?: string;
	isIndicative?: boolean;
	eventId?: string;
	cap?: number | null;
	min?: number | null;
	note?: string;
}

export interface InterchangeData {
	network: string;
	transactionType: string;
	amountEur: number;
	amountOriginal: number;
	currency: string;
	fxRate: number;
	interchange: InterchangeFee[];
	schemeFees: InterchangeFee[];
	switchingFees: InterchangeFee[];
	totalInterchangeEur: number;
	totalSchemeFeesEur: number;
	totalSwitchingFeesEur: number;
	totalEur: number;
	totalPercent: number;
}

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isFee = (value: unknown): value is InterchangeFee => record(value)
	&& typeof value.name === "string" && finite(value.amountEur)
	&& ["id", "service", "rateDescription", "basis", "source", "eventId", "note"].every(key => value[key] == null || typeof value[key] === "string")
	&& ["rate", "flat", "cap", "min"].every(key => value[key] == null || finite(value[key]))
	&& (value.isIndicative == null || typeof value.isIndicative === "boolean");

export function parseInterchangeData(raw: string): InterchangeData | null {
	try {
		let value: unknown = JSON.parse(raw);
		// The API can return JSON text or wrap its payload in data.
		for (let depth = 0; depth < 4; depth++) {
			if (typeof value === "string") value = JSON.parse(value);
			else if (record(value) && !("network" in value) && "data" in value) value = value.data;
			else break;
		}
		if (!record(value) || !["network", "transactionType", "currency"].every(key => typeof value[key] === "string")) return null;
		if (!["amountEur", "amountOriginal", "fxRate", "totalInterchangeEur", "totalSchemeFeesEur", "totalSwitchingFeesEur", "totalEur", "totalPercent"].every(key => finite(value[key]))) return null;
		if (!["interchange", "schemeFees", "switchingFees"].every(key => Array.isArray(value[key]) && value[key].every(isFee))) return null;
		return value as unknown as InterchangeData;
	} catch { return null; }
}

// Keep sub-cent fees visible; totals and fee amounts come from the API, not rate × amount.
export const feeNumber = (value: number, minimumFractionDigits = 2, locale = "ru-RU") => new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits: 8 }).format(value);
export const feeMoney = (value: number, currency = "EUR", locale = "ru-RU") => `${feeNumber(value, 2, locale)} ${currency}`;
export const feePercent = (value: number, locale = "ru-RU") => `${feeNumber(value, 0, locale)}%`;

const feeCurrencyCodes: Record<string, string> = {
	"€": "EUR", "US$": "USD", "$": "USD", "£": "GBP",
	"CA$": "CAD", "C$": "CAD", "AU$": "AUD", "A$": "AUD", "NZ$": "NZD", "HK$": "HKD", "SG$": "SGD",
};
const feeCurrencySymbol = Object.keys(feeCurrencyCodes).sort((a, b) => b.length - a.length).map(symbol => symbol.replace(/\$/g, "\\$")).join("|");
const feeTextAmount = "[+−-]?(?:\\d{1,3}(?:[ \\u00a0\\u202f]\\d{3})+|\\d+)(?:[.,]\\d+)*";
const prefixedFeeCurrency = new RegExp(`(?<![\\p{L}\\p{N}])(${feeCurrencySymbol})\\s*(${feeTextAmount})`, "gu");
const suffixedFeeCurrency = new RegExp(`(${feeTextAmount})\\s*(${feeCurrencySymbol})(?![\\p{L}\\p{N}])`, "gu");

// API descriptions may quote USD tariffs alongside EUR totals. Change notation
// only: preserve the quoted currency, decimal precision and original payload.
export const feeCurrencyText = (value?: string) => value
	?.replace(prefixedFeeCurrency, (_, symbol: string, amount: string) => `${amount} ${feeCurrencyCodes[symbol]}`)
	.replace(suffixedFeeCurrency, (_, amount: string, symbol: string) => `${amount} ${feeCurrencyCodes[symbol]}`);
